import { Component, DestroyRef, inject, signal } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import {
  ApiHttpService,
  ApplicationUserService,
  DetailActionService,
  MercureService,
  NavigationService,
  RouteEntitySubscriberService,
  RouteEntitySubscriberSetup,
  User,
  AppSubmitBarComponent,
  AppLoadErrorComponent,
  interfaceLanguages,
  LanguageKey,
} from 'shared';
import { TranslateService } from '@ngx-translate/core';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { distinctUntilChanged, filter, first, skip } from 'rxjs/operators';
import { combineLatest, map } from 'rxjs';
import { SkeletonComponent } from '@progress/kendo-angular-indicators';

@Component({
  selector: 'rc-app',
  standalone: true,
  imports: [RouterOutlet, DialogModule, SkeletonComponent, AppSubmitBarComponent, AppLoadErrorComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  #authService = inject(AuthService);
  #apiService = inject(ApiHttpService);
  #detailActionService = inject(DetailActionService);
  #applicationUserService = inject(ApplicationUserService);
  #navigationService = inject(NavigationService);
  #mercureService = inject(MercureService);
  #routeEntitySubscriberSetup = inject(RouteEntitySubscriberSetup);
  #tracker = inject(RouteEntitySubscriberService);
  #destroyRef = inject(DestroyRef);
  #router = inject(Router);
  #platformLocation = inject(PlatformLocation);
  #translateService = inject(TranslateService);

  user = signal<User | null>(null);
  appReady = signal(false);
  userSettings = toSignal(this.#applicationUserService.userSettings$);
  userLoadError = this.#applicationUserService.loadError;
  constructor() {
    this.redirectIfNotLoggedIn();

    this.#applicationUserService.setApplicationUser();

    // Keep local `user` signal in sync
    this.#applicationUserService.user$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((user) => this.user.set(user));

    // Show the app in the language of the user settings; a language without translations falls back to Dutch
    this.#applicationUserService.user$
      .pipe(
        filter((user): user is User => !!user),
        map((user) => user.settings?.language as LanguageKey),
        map((language) => (interfaceLanguages.includes(language) ? language : 'nl')),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((language) => this.#translateService.use(language));

    // When both auth and user are ready, call /cookie ONCE and then track entities
    combineLatest([this.#authService.isAuthenticated$, this.#applicationUserService.user$])
      .pipe(
        filter(([isAuth, user]) => !!isAuth && !!user),
        first(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#requestCookie());

    // Detail action cleanup
    this.#navigationService.updateDetailsActionEmitter.subscribe((value) => {
      if (value) {
        this.#mercureService.destroyEventEmitter.emit(true);
      }
    });

    // Clear detail actions on real navigation changes
    this.#router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        skip(1),
        map((e) => e.url),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((url) => {
        const newPath = url.split('?')[0];
        const currentPath = this.#router.url.split('?')[0];
        if (newPath !== currentPath) {
          this.#detailActionService.clear();
        }
      });
  }

  retryLoadUser(): void {
    if (this.#cookieFailed) {
      this.#cookieFailed = false;
      this.#applicationUserService.loadError.set(null);
      this.#requestCookie();
      return;
    }
    this.#applicationUserService.setApplicationUser();
  }

  /** Whether the /cookie request failed: the load error's retry then asks for it again. */
  #cookieFailed = false;

  #requestCookie(): void {
    this.#apiService
      .get('/cookie')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#routeEntitySubscriberSetup.trackEntities();
          // this.#tracker.start(); // if you ever want this again
          this.appReady.set(true);
        },
        // Without this the app stayed on its loading skeleton for good: show the load error with a retry instead
        error: (err) => {
          this.#cookieFailed = true;
          this.#applicationUserService.loadError.set(err);
        },
      });
  }

  // ngOnInit is now optional / not needed
  // ngOnInit(): void {}

  redirectIfNotLoggedIn(): void {
    this.#authService.error$.pipe(takeUntilDestroyed()).subscribe((error) => {
      if (error) {
        this.#loginWithRedirect();
      }
    });

    this.#authService.isAuthenticated$.pipe(takeUntilDestroyed()).subscribe((isAuthenticated) => {
      if (!isAuthenticated) {
        this.#loginWithRedirect();
      }
    });
  }

  /** After login, return to the requested page; the root url resolves to the dashboard. */
  #loginWithRedirect(): void {
    // The browser's url (PlatformLocation reads window.location), so the requested page is reopened after login
    const path = this.#platformLocation.pathname;
    const target = path === '/' ? '/dashboard' : path + this.#platformLocation.search;
    this.#authService.loginWithRedirect({
      appState: { target },
      authorizationParams: { prompt: 'login' },
    });
  }
}
